import type { AppMode } from '@sportybot-web/context/app-mode/context';

import { Group, Image, type ImageProps, Menu, Text, UnstyledButton } from '@mantine/core';
import { usePermissions } from '@sportybot-web/auth/permissions/use-permissions';
import { useAppMode } from '@sportybot-web/context/app-mode/use-app-mode';
import { Check, ChevronDown } from 'lucide-react';

import { APP_MODE_LOGO_SRC, APP_MODE_OPTIONS } from './constants';
import classes from './sportybot-logo.module.css';

interface AppModeLogoImageProps {
  appMode: AppMode;
  h: ImageProps['h'];
  w: ImageProps['w'];
}

export function SportybotLogo() {
  const { appMode, setAppMode } = useAppMode();
  const { can } = usePermissions();

  const logo = (
    <Group gap="space100" wrap="nowrap">
      <AppModeLogoImage appMode={appMode} h={{ base: 24, md: 32 }} w={{ base: 43, md: 58 }} />
      <Text span variant="heading-2">
        SPORTYBOT
      </Text>
    </Group>
  );

  if (!can('CAN_SWITCH_APP_MODE')) {
    return logo;
  }

  return (
    <Menu
      classNames={{ dropdown: classes.dropdown, item: classes.item }}
      position="bottom-start"
      width="target">
      <Menu.Target>
        <UnstyledButton
          aria-label={`Switch app mode, currently ${appMode}`}
          className={classes.trigger}>
          {logo}
          <ChevronDown size={16} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {APP_MODE_OPTIONS.map((option) => (
          <Menu.Item
            key={option}
            leftSection={<AppModeLogoImage appMode={option} h={24} w={43} />}
            onClick={() => setAppMode(option)}
            rightSection={option === appMode ? <Check size={16} /> : null}>
            {option}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function AppModeLogoImage({ appMode, h, w }: AppModeLogoImageProps) {
  return <Image alt={appMode} fit="contain" h={h} src={APP_MODE_LOGO_SRC[appMode]} w={w} />;
}
